/** SpecTab — Panel specification (name, code, voltage, phases, main breaker). */
import { Field } from "./_shared";

// v1.86b — small "✕" button next to clearable fields. Hoisted outside SpecTab
// to avoid React's "component defined during render" warning (each render
// would otherwise destroy + recreate the entire button subtree).
function ClearBtn({ field, disabled, onClear }) {
  return (
    <button type="button"
            onClick={() => onClear && onClear(field)}
            disabled={disabled}
            title={disabled ? "Already empty" : `Clear ${field}`}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 inline-flex items-center justify-center text-xs text-gray-400 hover:text-[#FF6B6B] disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
            data-testid={`spec-clear-${field}`}>✕</button>
  );
}

export default function SpecTab({ project, update, clear }) {
  return (
    <div className="max-w-3xl">
      <div className="label-tag mb-2">// 01 Specification</div>
      <h2 className="font-display text-2xl font-bold">Panel specification</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <Field label="Project name">
          <input value={project.name} onChange={(e) => update({ name: e.target.value })}
            className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
            data-testid="spec-name" />
        </Field>
        <Field label="Location">
          <div className="relative">
            <input value={project.location || ""} onChange={(e) => update({ location: e.target.value })}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 pr-8 font-mono text-sm focus:border-[#00E5FF] outline-none"
              data-testid="spec-location" />
            <ClearBtn field="location" disabled={!project.location} onClear={clear} />
          </div>
        </Field>
        <Field label="Panel type">
          <select value={project.panel_type} onChange={(e) => update({ panel_type: e.target.value })}
            className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
            data-testid="spec-panel-type">
            <option value="electrical_room">Electrical Room</option>
            <option value="mcc">Motor Control Center</option>
            <option value="distribution_board">Distribution Board</option>
            <option value="lighting_control_panel">Lighting Control Panel</option>
            <option value="heat_trace_panel">Heat Trace Panel</option>
            <option value="control_panel">Control Panel</option>
          </select>
        </Field>
        <Field label="Code standard">
          <select value={project.code_standard} onChange={(e) => update({ code_standard: e.target.value })}
            className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
            data-testid="spec-code">
            <option value="NEC">NEC</option>
            <option value="CEC">CEC</option>
            <option value="IEEE">IEEE</option>
          </select>
        </Field>
        <Field label="System voltage (V)">
          <select value={project.voltage} onChange={(e) => update({ voltage: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
            data-testid="spec-voltage">
            {[120, 208, 240, 277, 480, 600].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>
        <Field label="Phases">
          <select value={project.phases} onChange={(e) => update({ phases: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
            data-testid="spec-phases">
            <option value={1}>1φ</option>
            <option value={3}>3φ</option>
          </select>
        </Field>
        <Field label="Main breaker (A)">
          <input type="number" value={project.main_breaker_amps} onChange={(e) => update({ main_breaker_amps: Number(e.target.value) })}
            className="w-full bg-black/40 border border-white/10 px-3 py-2 font-mono text-sm focus:border-[#00E5FF] outline-none"
            data-testid="spec-main-breaker" />
        </Field>
        <Field label="Description">
          <div className="relative">
            <input value={project.description || ""} onChange={(e) => update({ description: e.target.value })}
              className="w-full bg-black/40 border border-white/10 px-3 py-2 pr-8 font-mono text-sm focus:border-[#00E5FF] outline-none"
              data-testid="spec-description" />
            <ClearBtn field="description" disabled={!project.description} onClear={clear} />
          </div>
        </Field>
      </div>

      <div className="mt-10 bp-grid border border-white/10 p-6">
        <div className="label-tag">// Live summary</div>
        <div className="mt-3 font-mono text-sm text-gray-300 leading-relaxed">
          <span className="text-white">{project.name}</span> · {project.code_standard} · {project.voltage}V {project.phases}φ ·
          Main {project.main_breaker_amps}A · Circuits {(project.circuits || []).length}
          {project.panel_type === "electrical_room" && (
            <>
              <br />
              <span className="label-tag !text-[#00E5FF]">DIMENSIONS</span>{" "}
              <span className="text-white">{project.length_in || 0}</span>{(project.unit_system === "metric" ? "m" : "in")} L ·{" "}
              <span className="text-white">{project.width_in || 0}</span>{(project.unit_system === "metric" ? "m" : "in")} W ·{" "}
              <span className="text-white">{project.depth_in || 0}</span>{(project.unit_system === "metric" ? "m" : "in")} D ·{" "}
              <span className="label-tag !text-[#00E5FF]">WEIGHT</span>{" "}
              <span className="text-white">{project.weight_lbs || 0}</span>{(project.unit_system === "metric" ? " kg" : " lbs")} ·{" "}
              <span className="label-tag !text-[#00E5FF]">FOOTPRINT</span>{" "}
              <span className="text-white">{((project.length_in || 0) * (project.width_in || 0)).toFixed(1)}</span>{(project.unit_system === "metric" ? " m²" : " in²")} ·{" "}
              <span className="label-tag !text-[#00E5FF]">EQUIPMENT LIST</span>{" "}
              <span className="text-white">{(project.equipment_list || []).length}</span> items
            </>
          )}
        </div>
      </div>
    </div>
  );
}
